import { useContext, useEffect, useState } from "react";
import styles from "./produtividade.module.css";
import { AuthContext } from "../../context/AuthContext";
import api from "../../services/api";
import BottomNav from "../../components/BottomNav";

// Tela "Minha Produtividade" do colaborador (pedido do usuário, 2026-10-23):
// mesma informação da tela "Consulta do Salvo" do CozimaxERP, mas só do
// próprio colaborador logado (user.codfun) e sempre do MÊS CORRENTE.
//
// NÃO calcula nada aqui - só lê o que o ERP já salvou (cpd098 = acumulado do
// mês, cpd090 = dia a dia, cpd122/cpd030 = meta da equipe de origem). Rotas do
// srvPortal (uServiceProdutividade.pas), as mesmas que o ERP consome:
//   GET /pro/produtividade/salvos-mensal?mes=yyyy-mm&codfun=
//   GET /pro/produtividade/funcionario-origem?codfun=

// Mês corrente em hora LOCAL (toISOString() devolveria UTC e viraria o mês
// errado na virada, à noite, no fuso do Brasil).
function mesCorrenteISO() {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

const NOMES_MES = ["Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho",
    "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro"];

function tituloMes(mes) {
    const [ano, m] = mes.split("-");
    return `${NOMES_MES[Number(m) - 1]} de ${ano}`;
}

// O backend traz o % cru, sem arredondar - o arredondamento é só de exibição.
function fmtPct(v) {
    if (v === null || v === undefined || isNaN(Number(v))) return "0";
    return (Math.round(Number(v) * 10) / 10).toString().replace(".", ",");
}

// Semáforo do mês em relação à meta (regra do usuário, 2026-10-23), medido em
// PONTOS percentuais abaixo da meta, não em % da meta:
//   na meta ou acima            -> verde
//   até 10 pontos abaixo        -> amarelo (meta 80: de 70 a 79,99)
//   mais de 10 pontos abaixo    -> vermelho (meta 80: abaixo de 70)
// Compara o valor já arredondado em 1 casa (o mesmo que a tela exibe), pra a
// cor nunca contradizer o número na tela. Sem meta cadastrada, devolve null e
// a tela usa a cor neutra.
function corStatusMeta(pct, meta) {
    if (pct == null || meta == null) return null;
    const diff = Math.round((pct - meta) * 10) / 10;
    if (diff >= 0) return "#059669";   // verde
    if (diff >= -10) return "#d97706"; // amarelo
    return "#dc2626";                  // vermelho
}

const COR_MES = "#7C3AED";   // linha tracejada = % acumulado do mês
const COR_META = "#059669";  // linha pontilhada = meta da equipe de origem
const COR_LINHA = "#2563eb"; // evolução diária - azul, de propósito fora do semáforo (verde/amarelo/vermelho)
// Linha da meta no gráfico: cinza-escuro, pra não se confundir com a linha do
// mês quando esta estiver verde (o card da meta continua verde).
const COR_META_LINHA = "#475569";

// Gráfico de linha em SVG puro (o app não usa recharts) - responsivo via
// viewBox. X = dia do mês, Y = % de produtividade do dia.
function GraficoEvolucao({ pontos, diasNoMes, pctMes, meta, corMes }) {
    const W = 340, H = 220;
    const pad = { t: 14, r: 12, b: 28, l: 36 };
    const iw = W - pad.l - pad.r;
    const ih = H - pad.t - pad.b;

    const maxDado = Math.max(100, ...pontos.map(p => p.pct), pctMes ?? 0, meta ?? 0);
    const yMax = Math.ceil(maxDado / 20) * 20;

    const x = (dia) => pad.l + ((dia - 1) / Math.max(diasNoMes - 1, 1)) * iw;
    const y = (pct) => pad.t + ih - (pct / yMax) * ih;

    const ticksY = [];
    for (let v = 0; v <= yMax; v += 20) ticksY.push(v);
    const ticksX = [1, 5, 10, 15, 20, 25, diasNoMes].filter((d, i, a) => d <= diasNoMes && a.indexOf(d) === i);

    const linha = pontos.map(p => `${x(p.dia)},${y(p.pct)}`).join(" ");

    return (
        <svg viewBox={`0 0 ${W} ${H}`} className={styles.grafico} role="img" aria-label="Evolução diária da produtividade no mês">
            {ticksY.map(v => (
                <g key={v}>
                    <line x1={pad.l} x2={W - pad.r} y1={y(v)} y2={y(v)} stroke="#e5e7eb" strokeDasharray="3 3" />
                    <text x={pad.l - 6} y={y(v) + 3} textAnchor="end" fontSize="9" fill="#6b7280">{v}%</text>
                </g>
            ))}
            {ticksX.map(d => (
                <text key={d} x={x(d)} y={H - 10} textAnchor="middle" fontSize="9" fill="#6b7280">{d}</text>
            ))}

            {meta != null && (
                <line x1={pad.l} x2={W - pad.r} y1={y(meta)} y2={y(meta)}
                    stroke={COR_META_LINHA} strokeWidth="2" strokeDasharray="2 3" />
            )}
            {pctMes != null && (
                <line x1={pad.l} x2={W - pad.r} y1={y(pctMes)} y2={y(pctMes)}
                    stroke={corMes} strokeWidth="2" strokeDasharray="6 4" />
            )}

            {pontos.length > 1 && (
                <polyline points={linha} fill="none" stroke={COR_LINHA} strokeWidth="2" strokeLinejoin="round" />
            )}
            {pontos.map(p => (
                <circle key={p.dia} cx={x(p.dia)} cy={y(p.pct)} r="3" fill={COR_LINHA}>
                    <title>{`Dia ${p.dia}: ${fmtPct(p.pct)}%`}</title>
                </circle>
            ))}
        </svg>
    );
}

export default function Produtividade() {
    const { user } = useContext(AuthContext);
    const mesAtual = mesCorrenteISO();
    // Sempre abre no mês corrente; as setas navegam pra trás e pra frente.
    const [mes, setMes] = useState(mesAtual);

    const [mensal, setMensal] = useState(null);
    const [origem, setOrigem] = useState(null);
    const [loading, setLoading] = useState(true);
    const [erro, setErro] = useState("");

    useEffect(() => {
        let ativo = true;
        setLoading(true);
        setErro("");
        api.get("/pro/produtividade/salvos-mensal", { params: { mes, codfun: user.codfun } })
            .then(r => { if (ativo) setMensal(r.data); })
            .catch(e => { if (ativo) { setMensal(null); setErro(e.message || "Não foi possível carregar sua produtividade."); } })
            .finally(() => { if (ativo) setLoading(false); });
        return () => { ativo = false; };
    }, [mes, user.codfun]);

    // A meta (equipe de origem) não depende do mês navegado - busca 1 vez só.
    // Falhar não pode derrubar a tela (é só um complemento), por isso o catch.
    // Obs.: é a meta ATUAL da equipe; meses antigos são comparados com ela.
    useEffect(() => {
        api.get("/pro/produtividade/funcionario-origem", { params: { codfun: user.codfun } })
            .then(r => setOrigem(r.data))
            .catch(() => setOrigem(null));
    }, [user.codfun]);

    // Soma/subtrai meses em cima de "yyyy-mm" (Date em hora local, dia 1, evita estourar o fim do mês)
    function mudarMes(delta) {
        const [a, m] = mes.split("-").map(Number);
        const d = new Date(a, m - 1 + delta, 1);
        setMes(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`);
    }
    // Não há dado salvo de mês futuro: "pra frente" só depois de ter voltado.
    const noMesAtual = mes >= mesAtual;

    const [ano, mesNum] = mes.split("-").map(Number);
    const diasNoMes = new Date(ano, mesNum, 0).getDate();

    const porDia = mensal?.porDia ?? [];
    const pontos = porDia.map(d => ({ dia: d.dia, pct: Number(d.produtividadePct) || 0 }));
    const temMeta = !!(origem?.temOrigem && origem?.temIndequ);
    const meta = temMeta ? Number(origem.indequOrigem) : null;
    const pctMes = mensal?.existe ? Number(mensal.produtividadePct) : null;
    const diffMeta = pctMes != null && meta != null ? Math.round((pctMes - meta) * 10) / 10 : null;
    const corStatus = corStatusMeta(pctMes, meta);
    // Fundo suave (cor + 12% de opacidade em hex) e borda na cor do status
    const estiloStatus = corStatus ? { background: corStatus + "1f", border: `2px solid ${corStatus}` } : undefined;

    return (
        <div className={styles.container}>
            <div className={styles.header}>
                <div className={styles.headerIcon}>📈</div>
                <div className={styles.headerTexto}>
                    <h2>Minha Produtividade</h2>
                    <p>Acompanhe seu resultado mês a mês</p>
                </div>
            </div>

            <div className={styles.navMes}>
                <button type="button" onClick={() => mudarMes(-1)} disabled={loading} aria-label="Mês anterior">‹</button>
                <strong>{tituloMes(mes)}</strong>
                <button type="button" onClick={() => mudarMes(1)} disabled={loading || noMesAtual} aria-label="Próximo mês">›</button>
            </div>

            {loading && <p className={styles.aviso}>Carregando...</p>}
            {erro && <p className={styles.erro}>{erro}</p>}

            {!loading && !erro && (
                <>
                    {!mensal?.existe ? (
                        <p className={styles.aviso}>Não há produtividade registrada para você neste mês.</p>
                    ) : (
                        <div className={styles.cards}>
                            {/* Meta em cima (linha inteira); "Produzido" e "Vs. meta" lado a lado
                                embaixo. Sem meta cadastrada, "Produzido" ocupa a linha toda. */}
                            {temMeta && (
                                <div className={`${styles.card} ${styles.cardMeta}`}>
                                    <span>🎯 Meta da equipe</span>
                                    <strong style={{ color: COR_META }}>{fmtPct(meta)}%</strong>
                                </div>
                            )}
                            <div className={`${styles.card} ${temMeta ? "" : styles.cardCheio}`} style={estiloStatus}>
                                <span>Produzido no mês</span>
                                <strong style={{ color: corStatus ?? COR_MES }}>{fmtPct(pctMes)}%</strong>
                            </div>
                            {temMeta && (
                                <div className={styles.card} style={estiloStatus}>
                                    <span>Vs. meta</span>
                                    <strong style={{ color: corStatus }}>
                                        {diffMeta >= 0 ? "▲ +" : "▼ "}{fmtPct(diffMeta)}%
                                    </strong>
                                </div>
                            )}
                        </div>
                    )}

                    {mensal?.existe && (
                        <p className={styles.equipe}>
                            {temMeta
                                ? <>Equipe de origem: <strong>{origem.desequOrigem || `Equipe #${origem.numequOrigem}`}</strong></>
                                : "Sua equipe de origem ainda não tem meta configurada."}
                        </p>
                    )}

                    {pontos.length > 0 && (
                        <div className={styles.boxGrafico}>
                            <h3>Evolução diária</h3>
                            <GraficoEvolucao pontos={pontos} diasNoMes={diasNoMes} pctMes={pctMes} meta={meta} corMes={corStatus ?? COR_MES} />
                            <div className={styles.legenda}>
                                <span><i style={{ background: COR_LINHA }} /> Produtividade do dia</span>
                                {pctMes != null && <span><i style={{ background: corStatus ?? COR_MES }} /> Mês ({fmtPct(pctMes)}%)</span>}
                                {temMeta && <span><i style={{ background: COR_META_LINHA }} /> Meta ({fmtPct(meta)}%)</span>}
                            </div>
                        </div>
                    )}
                </>
            )}
            <BottomNav />
        </div>
    );
}
